import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import css from './login.module.css';
import {signInWithProvider} from '../services/authService';
import {useToast} from '../context/ToastProvider';
import {Icon} from '../components/common/Icon';
import {Spinner} from '../components/common/ui';
import {PRIVACY_URL, TERMS_URL} from '../constants';
import {releaseBusyOnRefocus} from '../utils';
import posthog, {isPostHogConfigured} from '../posthog';
import owlHero from '../assets/owl_hero.png';

type Provider = 'google' | 'apple';

const CANCEL_CODES = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

export function LoginPage() {
  const {t} = useTranslation();
  const showToast = useToast();
  const [busy, setBusy] = useState<Provider | null>(null);

  const signIn = async (provider: Provider) => {
    if (busy) {
      return;
    }
    setBusy(provider);
    // If the popup is closed without picking an account, the SDK's
    // rejection can lag (or never fire under strict COOP) — unfreeze the
    // buttons once focus returns to this window.
    const cleanup = releaseBusyOnRefocus(() => setBusy(null));
    try {
      // Success flips the auth state — AppShell swaps to the app.
      await signInWithProvider(provider);
      if (isPostHogConfigured) {
        posthog.capture('sign_in_completed', {auth_provider: provider});
      }
    } catch (e) {
      const code = String((e as {code?: string})?.code ?? '');
      if (!CANCEL_CODES.has(code)) {
        console.warn('sign-in failed', e);
        showToast(t('ERROR_LOGIN'), 'error');
      }
    } finally {
      cleanup();
      setBusy(null);
    }
  };

  return (
    <div className={css.wrap}>
      <div className={css.card}>
        <div className={css.heroWrap}>
          <img className={css.hero} src={owlHero} alt="Owlly" />
        </div>
        <h1 className={css.title}>Owlly</h1>
        <p className={css.subtitle}>{t('WebLoginSubtitle')}</p>

        <button
          className={css.providerBtn}
          onClick={() => signIn('google')}
          disabled={busy !== null}
          type="button"
        >
          {busy === 'google' ? <Spinner /> : <Icon name="google" size={18} />}
          {t('ContinueWithGoogle')}
        </button>
        <button
          className={css.providerBtn}
          onClick={() => signIn('apple')}
          disabled={busy !== null}
          type="button"
        >
          {busy === 'apple' ? <Spinner /> : <Icon name="apple" size={18} />}
          {t('ContinueWithApple')}
        </button>

        <p className={css.secure}>{t('SecureText')}</p>
        <p className={css.legal}>
          <a href={TERMS_URL} target="_blank" rel="noreferrer">
            {t('TermsConditions')}
          </a>
          {' · '}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
            {t('PrivacyPolicy')}
          </a>
        </p>
      </div>
    </div>
  );
}
